/**
 * Child-photo storage layer — IndexedDB only, never the network.
 *
 * This module is the ONLY place the app stores photos of children.
 * There is no photo upload endpoint, no photo field on any Pydantic
 * schema, and no photo column in the database. Photos live on the
 * parent's device in IndexedDB, keyed by `childId`. Deleting the
 * app's storage (clearing site data, reinstalling) wipes the photos
 * and that is BY DESIGN. See `CLAUDE.md` for the hard constraint.
 *
 * Implementation: `idb-keyval` gives us a tiny Promise-based wrapper
 * around IndexedDB. We store raw `Blob` values because browsers can
 * render blobs via `URL.createObjectURL()` with zero extra work and
 * we don't need to manage base64 conversion for display.
 *
 * Callers are expected to revoke object URLs they create via
 * `getChildPhotoObjectURL` when the component unmounts — otherwise
 * the blob stays alive until page unload. The `usePhotoURL` React
 * hook (added alongside) encapsulates that lifecycle.
 */

import { createStore, del, entries, get, set } from "idb-keyval";

// Dedicated store — keeps MIC photos in their own IDB database so
// other idb-keyval callers (if any) don't share the keyspace.
const PHOTO_STORE = createStore("mic-photos-db", "photos");


/**
 * Store a child's photo on this device.
 *
 * Overwrites any existing photo for that child. The caller is
 * responsible for getting a Blob from a file input or camera capture
 * — see `PhotoPicker` for the UI wrapper.
 */
export async function setChildPhoto(childId: string, blob: Blob): Promise<void> {
  await set(childId, blob, PHOTO_STORE);
}

/**
 * Retrieve a child's photo Blob, or `null` if none is stored.
 *
 * Returns the raw Blob — callers that want to render it in an
 * `<img>` should call `URL.createObjectURL()` and track the URL so
 * they can revoke it on unmount.
 */
export async function getChildPhotoBlob(childId: string): Promise<Blob | null> {
  const value = await get<Blob>(childId, PHOTO_STORE);
  return value ?? null;
}

/**
 * Convenience: load a child's photo and return an object URL for it,
 * or `null` if no photo is stored. The caller MUST revoke the URL
 * when it's no longer needed, e.g.:
 *
 *     const url = await getChildPhotoObjectURL(id);
 *     try { imgEl.src = url ?? ""; } finally { if (url) URL.revokeObjectURL(url); }
 *
 * Most components will use the `usePhotoURL` hook instead of this
 * helper directly — the hook handles revocation on unmount.
 */
export async function getChildPhotoObjectURL(childId: string): Promise<string | null> {
  const blob = await getChildPhotoBlob(childId);
  if (blob === null) return null;
  return URL.createObjectURL(blob);
}

/**
 * Remove a child's photo from this device.
 *
 * Called after `DELETE /api/children/{id}` succeeds — the server
 * row is gone, so the local photo has no owner and should be
 * cleaned up. Also safe to call when the user explicitly chooses
 * "Remove photo" in the edit form.
 */
export async function deleteChildPhoto(childId: string): Promise<void> {
  await del(childId, PHOTO_STORE);
}

/**
 * List every child_id that currently has a photo stored on this
 * device. Useful for the "orphan sweep" — on dashboard load we can
 * compare against the server's child list and drop photos whose
 * child was deleted from another device.
 */
export async function listStoredPhotoKeys(): Promise<string[]> {
  const all = await entries<string, Blob>(PHOTO_STORE);
  return all.map(([key]) => String(key));
}

/**
 * Remove any stored photos whose child_id is NOT in `currentChildIds`.
 *
 * Cross-device cleanup: if your spouse deletes a child on their
 * phone, your device still has the photo until next dashboard load.
 * Call this once after fetching `/api/children` to drop stragglers.
 */
export async function pruneOrphanedPhotos(
  currentChildIds: readonly string[],
): Promise<number> {
  const stored = await listStoredPhotoKeys();
  const valid = new Set(currentChildIds);
  const orphans = stored.filter((key) => !valid.has(key));
  await Promise.all(orphans.map((key) => del(key, PHOTO_STORE)));
  return orphans.length;
}
